import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Info,
  Package,
  Search,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import { RitiroCella } from "@/components/m1/RitiroCella";
import { num } from "@/lib/numeri";

import { CHAT_AGENTE_W } from "@/components/agenti/ChatAgente";
import { Paginazione } from "@/components/Paginazione";
import { AnticipaDialog } from "@/components/m1/AnticipaDialog";
import { CruscottoM1 } from "@/components/m1/CruscottoM1";
import { IrisDock } from "@/components/m1/IrisDock";
import { DoveLaMerceCella } from "@/components/m1/DoveLaMerceBarra";
import { ImpostazioniM1 } from "@/components/m1/ImpostazioniM1";
import { ImpostazioniOverlay } from "@/components/ImpostazioniOverlay";
import { MagazzinoM1 } from "@/components/m1/MagazzinoM1";
import { RigaTesta } from "@/components/RigaTesta";
import { TabModulo } from "@/components/TabModulo";
import { SEZIONI_M1 } from "@/lib/nav-config";
import { PageContainer } from "@/components/PageContainer";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useModuli } from "@/hooks/useModuli";
import { useMyPermessi } from "@/hooks/usePermessi";
import { hasPermessoSezione } from "@/lib/permessi";
import {
  useM1Dashboard,
  useM1Diagnosi,
  useM1Ordini,
  type OrdiniOrderBy,
  type OrdiniOrderDir,
} from "@/hooks/useM1";
import {
  useM3CreaCommessaDaM1,
  useM3StatoLogisticoPerOrdini,
} from "@/hooks/useM3";
import { cn } from "@/lib/utils";
import type {
  CreaCommessaLogisticaBody,
  M1OrdineRaggruppato,
  M1OrdineRiga,
  StatoLogisticoOrdine,
  StatoRitiro,
} from "@/types/api";

import { NotFoundPage } from "./NotFoundPage";

const SLUG = "order-readiness";
const PAGE_SIZE = 25;

/**
 * I tre stati di ritiro (modello del 18-08-2026), con la ragione di ciascuno.
 *
 * `tassativa` è **derivata**: cliccarla filtra, ma non si marca — la fa il ritiro
 * fissato. Se fosse un'opzione da spuntare esisterebbero tassative senza ritiro,
 * cioè promesse scoperte da inseguire.
 */
const STATI_RITIRO: {
  value: StatoRitiro;
  label: string;
  sotto: string;
  tono?: "ok" | "warn" | "alert";
  info: string;
}[] = [
  {
    value: "tassativa",
    label: "Tassative",
    sotto: "ritiro fissato",
    tono: "ok",
    info: "Il ritiro è già fissato: la promessa è coperta. È uno stato derivato, non si marca — e per questo non possono esistere tassative senza ritiro.",
  },
  {
    value: "standard",
    label: "Standard",
    sotto: "ritiro da fissare",
    info: "Si produce entro la data prevista, il ritiro non è ancora stato fissato. È il default.",
  },
  {
    value: "a_chiamata",
    label: "A chiamata",
    sotto: "riempie il magazzino",
    tono: "warn",
    info: "Il cliente ritira quando gli serve: la merce resta nel nostro magazzino e la data non è certa. Si marca dalla colonna «Ritiro», sulla commessa o su tutto il cliente.",
  },
];



const STATI = [
  { value: "00 - EVADIBILE PUNTUALE", label: "Puntuale", tone: "ok" },
  { value: "01 - EVADIBILE IN RITARDO", label: "In ritardo", tone: "warn" },
  { value: "02 - PARZIALMENTE EVADIBILE", label: "Parziale", tone: "warn" },
  { value: "03 - TOTALMENTE INEVADIBILE", label: "Inevadibile", tone: "alert" },
] as const;

const STATO_TONE_CLASS: Record<string, string> = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warn: "bg-amber-50 text-amber-800 border-amber-200",
  alert: "bg-red-50 text-red-700 border-red-200",
  neutral: "bg-muted text-muted-foreground border-border",
};

function statoTone(stato: string | null): "ok" | "warn" | "alert" | "neutral" {
  if (!stato) return "neutral";
  const m = STATI.find((s) => s.value === stato);
  return m ? (m.tone as "ok" | "warn" | "alert") : "neutral";
}

function statoLabel(stato: string | null): string {
  if (!stato) return "—";
  const m = STATI.find((s) => s.value === stato);
  return m ? m.label : stato;
}

function formatDataIt(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}


/**
 * ⚠️ `formatNum` non raggruppava i numeri di quattro cifre: `toLocaleString("it-IT")`
 * senza `useGrouping` salta il separatore sotto le cinque cifre (l'ICU italiano ha
 * `minimumGroupingDigits = 2`). Effetto: la colonna diceva «1201» e la riga sotto la
 * barra «1.201 pronti» — lo stesso numero, due formati, uno accanto all'altro, su
 * 116 commesse su 201. Tre componenti di M1 lo avevano già corretto, la pagina no.
 * Ora il formato sta in un posto solo.
 */
const formatNum = num;

export function M1OrderReadinessPage() {
  const { data: moduli = [], isLoading: loadingModuli } = useModuli();

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  // `segnali` resta accettato come alias di `magazzino`: era l'indirizzo dei
  // link vecchi (il widget KPI della dashboard ci punta ancora).
  const tab: "cruscotto" | "portafoglio" | "magazzino" =
    tabParam === "portafoglio"
      ? "portafoglio"
      : tabParam === "magazzino" || tabParam === "segnali"
        ? "magazzino"
        : "cruscotto";

  // L'ingranaggio: `?tab=impostazioni` resta un indirizzo valido e apre il
  // pannello, come in M2 e M3 — così un link salvato non si rompe e la sezione
  // ha comunque un permesso proprio (`nav_voce.m1.impostazioni`, che eredita
  // dal modulo finché nessuno lo spegne).
  const perms = useMyPermessi().data;
  const puoImpostazioni = hasPermessoSezione(perms, "m1.impostazioni", "M1");
  const [impostazioniAperte, setImpostazioniAperte] = useState(tabParam === "impostazioni");

  // Le porte del Cruscotto aprono il Portafoglio già filtrato, quindi i filtri
  // vivono nell'URL e basta: tenerne anche una copia in `useState` significava
  // che arrivando a pagina già montata il filtro non si applicava.
  const statoDaUrl = searchParams.get("stato");
  const statiSelezionati = useMemo(() => (statoDaUrl ? [statoDaUrl] : []), [statoDaUrl]);
  // Lo stato di **ritiro**, che è un asse diverso dall'evadibilità: `?ritiro=` era
  // l'indirizzo a cui il Cruscotto navigava e che nessuno leggeva — né la pagina
  // né l'API. Ora lo leggono entrambi.
  const ritiroDaUrl = searchParams.get("ritiro") as StatoRitiro | null;
  const ritiriSelezionati = useMemo(
    () => (ritiroDaUrl ? [ritiroDaUrl] : []),
    [ritiroDaUrl],
  );
  // Le tassative sforate: non è un quarto stato di ritiro, è un asse a parte, e
  // fino a ieri era un numero che si poteva leggere ma non aprire.
  const ritiroScadutoDaUrl = searchParams.get("ritiro_scaduto") === "1";
  const nonValutateDaUrl = searchParams.get("non_valutate") === "1";
  // La porta «NDP senza merce» del Cruscotto (23-09): nota di prelievo emessa e
  // merce che non risulta a magazzino.
  const ndpSenzaMerceDaUrl = searchParams.get("ndp_senza_merce") === "1";
  const soloDivergenze = searchParams.get("discordanze") === "1";
  const clienteDaUrl = searchParams.get("cliente");

  /** Scrive i filtri nell'URL: un gesto, un indirizzo condivisibile. */
  function aggiornaFiltri(muta: (p: URLSearchParams) => void) {
    const p = new URLSearchParams(searchParams);
    muta(p);
    setSearchParams(p, { replace: true });
    setPage(0);
  }
  // L'avviso dopo una marcatura di ritiro. Serve perché marcare un **cliente**
  // cambia lo stato di tutte le sue commesse: senza dirlo, chi ha premuto crede
  // di averne toccata una. Stessa lezione della spunta «materiale di consumo»,
  // dove escludendo un articolo non succedeva niente di visibile (difetto trovato
  // da Enrico il 16-08).
  const [avvisoRitiro, setAvvisoRitiro] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [soloAperti, setSoloAperti] = useState(true);
  const [page, setPage] = useState(0);
  const [expandedOrdine, setExpandedOrdine] = useState<number | null>(null);
  const [expandedRiga, setExpandedRiga] = useState<string | null>(null);
  // Iris occupa spazio come Aida (forma di riferimento scelta il 16-08): il
  // contenuto si stringe invece di finire sotto al pannello.
  const [irisAperta, setIrisAperta] = useState(false);
  const [orderBy, setOrderBy] = useState<OrdiniOrderBy>("data_consegna");
  const [orderDir, setOrderDir] = useState<OrdiniOrderDir>("asc");

  function toggleSort(col: OrdiniOrderBy) {
    if (orderBy !== col) {
      setOrderBy(col);
      setOrderDir(col === "data_consegna" ? "asc" : "desc");
    } else if (orderDir === "asc") {
      setOrderDir("desc");
    } else {
      // terzo click: torna al default (data_consegna asc).
      setOrderBy("data_consegna");
      setOrderDir("asc");
    }
    setPage(0);
  }

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch((corrente) => {
        const voluto = searchInput.trim();
        if (voluto !== corrente) setPage(0);
        return voluto;
      });
    }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const ordiniOpts = useMemo(
    () => ({
      stato_maitime: statiSelezionati.length ? statiSelezionati : undefined,
      stato_ritiro: ritiriSelezionati.length ? ritiriSelezionati : undefined,
      ritiro_scaduto: ritiroScadutoDaUrl,
      non_valutate: nonValutateDaUrl,
      ndp_senza_merce: ndpSenzaMerceDaUrl,
      solo_divergenze: soloDivergenze,
      cliente_codice: clienteDaUrl ? Number(clienteDaUrl) : undefined,
      q: search || undefined,
      solo_aperti: soloAperti,
      order_by: orderBy,
      order_dir: orderDir,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [
      statiSelezionati,
      ritiriSelezionati,
      ritiroScadutoDaUrl,
      nonValutateDaUrl,
      ndpSenzaMerceDaUrl,
      soloDivergenze,
      clienteDaUrl,
      search,
      soloAperti,
      orderBy,
      orderDir,
      page,
    ]
  );

  const { data: dashboard, isLoading: loadingDash } = useM1Dashboard();
  const { data: ordini, isLoading: loadingOrdini, isFetching } = useM1Ordini(ordiniOpts);

  // Stato logistico per le commesse mostrate in tabella
  const numeriOrdiniVisibili = useMemo(
    () => (ordini?.items ?? []).map((o) => o.numero_ordine),
    [ordini]
  );
  const { data: statoLogisticoResp } = useM3StatoLogisticoPerOrdini(numeriOrdiniVisibili);
  const statoLogisticoMap = useMemo(() => {
    const map = new Map<number, StatoLogisticoOrdine>();
    for (const s of statoLogisticoResp?.items ?? []) map.set(s.numero_ordine, s);
    return map;
  }, [statoLogisticoResp]);

  const creaCommessa = useM3CreaCommessaDaM1();
  const [anticipaTarget, setAnticipaTarget] = useState<M1OrdineRaggruppato | null>(null);

  function handlePassa(ord: M1OrdineRaggruppato) {
    if (!confirm(`Passare la commessa #${ord.numero_ordine} a Logistica come pronta?`)) return;
    creaCommessa.mutate(
      { numero_ordine: ord.numero_ordine, pipeline_tipo: "effettiva" } as CreaCommessaLogisticaBody,
      {
        onSuccess: () => {
          // optional: redirect a M3 con la commessa selezionata
        },
        onError: (err: unknown) => {
          alert(`Errore: ${err instanceof Error ? err.message : String(err)}`);
        },
      }
    );
  }

  function handleAnticipa(ord: M1OrdineRaggruppato) {
    setAnticipaTarget(ord);
  }

  if (loadingModuli) {
    return (
      <PageContainer>
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-32 w-full" />
      </PageContainer>
    );
  }

  const modulo = moduli.find((m) => m.slug === SLUG);
  if (!modulo) return <NotFoundPage />;


  /** Un riquadro alla volta: cliccarne uno sostituisce il filtro, non lo somma. */
  function soloQuestoStato(value: string) {
    aggiornaFiltri((p) => {
      p.delete("discordanze");
      p.delete("non_valutate");
      if (statoDaUrl === value) p.delete("stato");
      else p.set("stato", value);
    });
  }

  function soloQuestoRitiro(value: StatoRitiro) {
    aggiornaFiltri((p) => {
      p.delete("ritiro_scaduto");
      if (ritiroDaUrl === value) p.delete("ritiro");
      else p.set("ritiro", value);
    });
  }

  function soloNonValutate() {
    aggiornaFiltri((p) => {
      p.delete("stato");
      p.delete("discordanze");
      if (nonValutateDaUrl) p.delete("non_valutate");
      else p.set("non_valutate", "1");
    });
  }

  function soloRitiroScaduto() {
    aggiornaFiltri((p) => {
      p.delete("ritiro");
      if (ritiroScadutoDaUrl) p.delete("ritiro_scaduto");
      else p.set("ritiro_scaduto", "1");
    });
  }

  function clearFilters() {
    setSearch("");
    setSearchInput("");
    setSoloAperti(true);
    aggiornaFiltri((p) => {
      [
        "stato",
        "discordanze",
        "cliente",
        "ritiro",
        "ritiro_scaduto",
        "non_valutate",
        "ndp_senza_merce",
      ].forEach((k) => p.delete(k));
    });
  }

  function submitSearch(e: React.FormEvent) {
    // L'invio resta valido, ma la ricerca parte già da sola mentre si scrive.
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(0);
  }

  function toggleOrdine(numero: number) {
    setExpandedOrdine((cur) => (cur === numero ? null : numero));
    setExpandedRiga(null);
  }

  function toggleRiga(id: string) {
    setExpandedRiga((cur) => (cur === id ? null : id));
  }

  // Quando si arriva dal Cruscotto filtrati su un cliente, la riga di testa lo
  // dice: altrimenti la tabella sembra vuota senza spiegazione.
  const clienteNome = clienteDaUrl
    ? (ordini?.items[0]?.cliente_ragione_sociale ?? `cliente ${clienteDaUrl}`)
    : null;

  const hasFilters =
    statiSelezionati.length > 0 ||
    ritiriSelezionati.length > 0 ||
    ritiroScadutoDaUrl ||
    nonValutateDaUrl ||
    ndpSenzaMerceDaUrl ||
    soloDivergenze ||
    !!clienteDaUrl ||
    search.length > 0 ||
    !soloAperti;

  return (
    <>
      {/* Il contenuto si stringe quando Iris è aperta, come nel guscio di M3:
          il pannello occupa spazio, non copre. Sotto `lg` la chat non c'è e il
          padding non serve. */}
      <div
        className={cn(
          "transition-[padding] duration-200",
          irisAperta ? "lg:pr-[var(--iris-w)]" : "lg:pr-11",
        )}
        style={{ "--iris-w": `${CHAT_AGENTE_W}px` } as React.CSSProperties}
      >
    <PageContainer>
      <TabModulo
        sezioni={SEZIONI_M1}
        modulo="M1"
        ingranaggioVisibile={puoImpostazioni}
        onIngranaggio={() => setImpostazioniAperte(true)}
      />

      {tab === "cruscotto" && <CruscottoM1 dashboard={dashboard} />}
      {tab === "magazzino" && <MagazzinoM1 />}

      {tab === "portafoglio" && (
      <div className="space-y-4">
      <RigaTesta
        contesto={ordini ? `${formatNum(ordini.total)} commesse` : undefined}
        caricatoIl={dashboard?.snapshot_caricato_il}
        destra={
          clienteNome
            ? `filtrato su ${clienteNome}`
            : ndpSenzaMerceDaUrl
              ? // Un filtro che nessun riquadro accende deve dirsi da solo,
                // altrimenti la tabella sembra corta senza una ragione.
                "NDP emessa e merce non a magazzino"
              : undefined
        }
      />

      {/* I numeri SONO i filtri, come nel tabellone M3. Qui e non nel Cruscotto:
          servono mentre si lavora sulla tabella, non nella lettura d'insieme. */}
      <section className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
        <RiquadroFiltro
          label="Tutte"
          valore={dashboard?.ordini_totali}
          sotto={dashboard ? `${formatNum(dashboard.righe_totali)} righe` : undefined}
          attivo={
            !statiSelezionati.length &&
            !ritiriSelezionati.length &&
            !ritiroScadutoDaUrl &&
            !nonValutateDaUrl &&
            !soloDivergenze &&
            !clienteDaUrl
          }
          loading={loadingDash}
          onClick={clearFilters}
        />
        {STATI.map((st) => (
          <RiquadroFiltro
            key={st.value}
            label={st.label}
            valore={byStato(dashboard?.ordini_per_stato_maitime, st.value)}
            sotto={
              loadingDash
                ? undefined
                : `${formatNum(byStato(dashboard?.per_stato_maitime, st.value))} righe`
            }
            tono={st.tone}
            attivo={
              !soloDivergenze &&
              statiSelezionati.length === 1 &&
              statiSelezionati[0] === st.value
            }
            loading={loadingDash}
            onClick={() => soloQuestoStato(st.value)}
          />
        ))}
        {!!dashboard?.ordini_non_valutati && (
          <RiquadroFiltro
            label="Non valutate"
            valore={dashboard.ordini_non_valutati}
            sotto="solo materiale di consumo"
            attivo={nonValutateDaUrl}
            loading={loadingDash}
            onClick={soloNonValutate}
            info="Commesse i cui articoli sono tutti marcati «materiale di consumo»: il motore non le valuta, quindi non hanno uno stato di evadibilità. Prima non si contavano e i riquadri qui sopra non tornavano al totale."
          />
        )}
      </section>

      {/* Seconda fila: lo stato del **ritiro**, che è un asse diverso
          dall'evadibilità. Stavano insieme e «A chiamata» sembrava un pari di
          «Inevadibile» quando risponde a un'altra domanda: non «la merce c'è?» ma
          «quando esce, e chi l'ha promesso?». */}
      <section className="space-y-1.5">
        <p className="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
          Ritiro{" "}
          <span className="font-normal normal-case tracking-normal">
            — «tassativa» non si marca: la fa il ritiro fissato
          </span>
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {STATI_RITIRO.map((sr) => (
            <RiquadroFiltro
              key={sr.value}
              label={sr.label}
              valore={byStato(dashboard?.ordini_per_stato_ritiro, sr.value)}
              sotto={sr.sotto}
              tono={sr.tono}
              attivo={ritiriSelezionati[0] === sr.value}
              loading={loadingDash}
              disabilitato={!byStato(dashboard?.ordini_per_stato_ritiro, sr.value)}
              onClick={() => soloQuestoRitiro(sr.value)}
              info={sr.info}
            />
          ))}
          <RiquadroFiltro
            label="Ritiro scaduto"
            valore={dashboard?.ordini_ritiro_scaduto}
            sotto={
              dashboard?.ordini_ritiro_scaduto ? "data passata" : "nessuno"
            }
            tono="alert"
            attivo={ritiroScadutoDaUrl}
            loading={loadingDash}
            disabilitato={!dashboard?.ordini_ritiro_scaduto}
            onClick={soloRitiroScaduto}
            info="Il ritiro era fissato e la data è passata: o la merce è uscita e nessuno ha chiuso la commessa, o il ritiro è saltato e nessuno l'ha ripreso."
          />
        </div>
      </section>

      {avvisoRitiro && (
        <div className="flex items-start gap-2 rounded-lg border border-primary/25 bg-accent px-3 py-2 text-xs text-accent-foreground">
          <span className="flex-1">{avvisoRitiro}</span>
          <button
            type="button"
            onClick={() => setAvvisoRitiro(null)}
            className="font-semibold underline"
          >
            ok
          </button>
        </div>
      )}
      <section className="space-y-3">
        <div className="space-y-1">
          <h2 className="text-base font-semibold inline-flex items-center gap-2">
            Portafoglio commesse
            <InfoTooltip>
              <>
                Lista <strong>completa</strong> delle commesse del snapshot M1 corrente.
                Ogni commessa ha una valutazione di evadibilità calcolata da Maitime sulle
                giacenze + piano di confezionamento.
                <br />
                <br />
                Filtra per stato cliccando i riquadri qui sopra; cerca per cliente,
                commessa o articolo; espandi una commessa per vedere le righe, e una riga
                per la diagnosi (perché Maitime ha calcolato quello stato).
              </>
            </InfoTooltip>
          </h2>
          <p className="text-xs text-muted-foreground">
            Ogni commessa dice dove si trova la sua merce: quanta è già stata ritirata,
            quanta è pronta a magazzino, quanta è in produzione, quanta è da produrre.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={submitSearch} className="relative min-w-[18rem] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Cerca cliente, commessa, riferimento del cliente, articolo…"
              className="h-9 w-full rounded-md border bg-card pl-9 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </form>

          <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={soloAperti}
              onChange={(e) => {
                setSoloAperti(e.target.checked);
                setPage(0);
              }}
              className="rounded"
            />
            Solo righe aperte
          </label>

          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="size-3" />
              Pulisci filtri
            </button>
          )}
        </div>
      </section>

      {/* Tabella commesse raggruppate */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-medium text-foreground">
            Commesse{" "}
            {ordini ? (
              <span className="text-muted-foreground font-normal">· {formatNum(ordini.total)}</span>
            ) : null}
          </h2>
          {isFetching && <span className="text-xs text-muted-foreground">aggiornamento…</span>}
        </div>

        {loadingOrdini ? (
          <Skeleton className="h-64 w-full" />
        ) : !ordini || ordini.items.length === 0 ? (
          <div className="rounded-lg border border-dashed bg-card/40 px-6 py-12 text-center">
            <AlertCircle className="size-5 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              Nessuna commessa corrisponde ai filtri selezionati.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="w-8" />
                    <SortableTh
                      label="Commessa"
                      col="numero_ordine"
                      orderBy={orderBy}
                      orderDir={orderDir}
                      onToggle={toggleSort}
                    />
                    <SortableTh
                      label="Cliente"
                      col="cliente"
                      orderBy={orderBy}
                      orderDir={orderDir}
                      onToggle={toggleSort}
                    />
                    <th className="text-left font-medium px-3 py-2">Ritiro</th>
                    <th className="text-left font-medium px-3 py-2">Dov&apos;è la merce</th>
                    <SortableTh
                      label="Residua / Ord."
                      col="qty_residua"
                      align="right"
                      orderBy={orderBy}
                      orderDir={orderDir}
                      onToggle={toggleSort}
                    />
                    <SortableTh
                      label="Consegna"
                      col="data_consegna"
                      orderBy={orderBy}
                      orderDir={orderDir}
                      onToggle={toggleSort}
                    />
                    <SortableTh
                      label="Stato Maitime"
                      col="stato_maitime"
                      orderBy={orderBy}
                      orderDir={orderDir}
                      onToggle={toggleSort}
                    />
                    <th className="text-left font-medium px-3 py-2">Logistica</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {ordini.items.map((ord) => (
                    <OrdineGruppoRow
                      key={ord.numero_ordine}
                      ordine={ord}
                      expanded={expandedOrdine === ord.numero_ordine}
                      onToggle={() => toggleOrdine(ord.numero_ordine)}
                      onRitiroApplicato={setAvvisoRitiro}
                      expandedRiga={expandedRiga}
                      onToggleRiga={toggleRiga}
                      statoLogistico={statoLogisticoMap.get(ord.numero_ordine)}
                      onPassa={handlePassa}
                      onAnticipa={handleAnticipa}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* La stessa barra del Magazzino: due liste nella stessa pagina che
                si sfogliano in due modi sarebbero due cose da imparare. */}
            <Paginazione
              pagina={page}
              perPagina={PAGE_SIZE}
              totale={ordini.total}
              unita="commesse"
              onVai={setPage}
              formatta={formatNum}
            />
          </div>
        )}
      </section>

      <p className="text-[11px] text-muted-foreground italic">
        Lo stato della commessa è il peggior caso fra le sue righe. Lo stato Maitime è
        ricalcolato in modo trasparente confrontando giacenza interna e piano di
        confezionamento con la quantità ancora da evadere. Espandi una commessa per vedere
        le righe, espandi una riga per le cause.
      </p>
      </div>
      )}

      <AnticipaDialog
        ordine={anticipaTarget}
        onClose={() => setAnticipaTarget(null)}
        isPending={creaCommessa.isPending}
        onConfirm={(data) => {
          if (!anticipaTarget) return;
          creaCommessa.mutate(
            {
              numero_ordine: anticipaTarget.numero_ordine,
              pipeline_tipo: "anticipo",
              data_pronto_prevista: data,
            } as CreaCommessaLogisticaBody,
            {
              onSuccess: () => setAnticipaTarget(null),
              onError: (err: unknown) =>
                alert(`Errore: ${err instanceof Error ? err.message : String(err)}`),
            }
          );
        }}
      />
    </PageContainer>
      </div>

      {/* Iris sta su tutte e tre le sezioni: il contesto lo dà la pagina. */}
      <div className="fixed bottom-0 right-0 top-14 z-30 hidden lg:block">
        <IrisDock sezione={tab} aperta={irisAperta} onApertaChange={setIrisAperta} />
      </div>

      {impostazioniAperte && (
        <ImpostazioniOverlay
          titolo="Impostazioni di Commesse"
          onChiudi={() => setImpostazioniAperte(false)}
        >
          <ImpostazioniM1 />
        </ImpostazioniOverlay>
      )}
    </>
  );
}

interface OrdineGruppoRowProps {
  ordine: M1OrdineRaggruppato;
  expanded: boolean;
  onToggle: () => void;
  /** Il messaggio dopo una marcatura: chi marca un cliente ne tocca undici. */
  onRitiroApplicato: (messaggio: string) => void;
  expandedRiga: string | null;
  onToggleRiga: (id: string) => void;
  statoLogistico: StatoLogisticoOrdine | undefined;
  onPassa: (ord: M1OrdineRaggruppato) => void;
  onAnticipa: (ord: M1OrdineRaggruppato) => void;
}

function OrdineGruppoRow({
  ordine: o,
  expanded,
  onToggle,
  onRitiroApplicato,
  expandedRiga,
  onToggleRiga,
  statoLogistico,
  onPassa,
  onAnticipa,
}: OrdineGruppoRowProps) {
  const toneM = statoTone(o.stato_maitime_aggregato);
  const toneClsM = STATO_TONE_CLASS[toneM];

  // Evidenziazione riga: commesse evadibili (puntuali o in ritardo) hanno sfondo
  // colorato sottile per emergere a colpo d'occhio nel portafoglio. Le commesse
  // parzialmente evadibili o totalmente inevadibili restano neutre.
  const rowBg =
    o.stato_maitime_aggregato === "00 - EVADIBILE PUNTUALE"
      ? "bg-emerald-50/50 hover:bg-emerald-100/60"
      : o.stato_maitime_aggregato === "01 - EVADIBILE IN RITARDO"
        ? "bg-amber-50/40 hover:bg-amber-100/60"
        : "hover:bg-accent/30";

  return (
    <>
      <tr
        className={cn(
          rowBg,
          "cursor-pointer",
          expanded && "ring-1 ring-inset ring-accent/40"
        )}
        onClick={onToggle}
      >
        <td className="px-2">
          {expanded ? (
            <ChevronDown className="size-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3.5 text-muted-foreground" />
          )}
        </td>
        <td className="px-3 py-2">
          <span className="font-mono text-xs">{o.numero_ordine}</span>
          {/* Come la chiama il cliente: è il numero che scrive nelle mail, e a
              Marina serve per riconoscerla (01-10-2026). */}
          {o.riferimento_cliente && (
            <span
              className="block max-w-[9rem] truncate text-[10.5px] text-muted-foreground"
              title={`Riferimento del cliente: ${o.riferimento_cliente}`}
              data-testid="rif-cliente"
            >
              rif. {o.riferimento_cliente}
            </span>
          )}
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-foreground truncate max-w-[15rem]">
              {o.cliente_ragione_sociale}
            </span>
            {o.note_memoria > 0 && (
              <span
                className="shrink-0 rounded-full bg-brand-50 px-1.5 text-[10px] font-semibold text-brand-700 dark:bg-brand-950 dark:text-brand-300"
                title="Note dei commerciali in Memoria per questa commessa o per il cliente"
              >
                {o.note_memoria} {o.note_memoria === 1 ? "nota" : "note"}
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground tabular-nums">
            {o.cliente_citta ? o.cliente_citta : `cod. ${o.cliente_codice}`}
          </div>
        </td>
        <td className="px-3 py-2">
          <RitiroCella ordine={o} onApplicato={onRitiroApplicato} />
        </td>
        <td className="px-3 py-2 w-[8.5rem]">
          <DoveLaMerceCella merce={o.dove_la_merce} />
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          <div className="font-medium">{formatNum(o.qty_residua_totale)}</div>
          <div className="text-[11px] text-muted-foreground">
            /{formatNum(o.qty_ordinata_totale)}
          </div>
        </td>
        {/* La data del gestionale, senza allarme: per molte commesse non è una
            promessa al cliente ma la leva con cui si è fatta produrre la merce
            (Enrico, 16-08). Segnalare un ritardo su quella data grida qualcosa
            che spesso non è nostro. */}
        <td className="px-3 py-2 text-xs whitespace-nowrap text-muted-foreground">
          {formatDataIt(o.data_consegna_minima)}
        </td>
        <td className="px-3 py-2">
          <span
            className={cn(
              "inline-block px-2 py-0.5 rounded-full text-[11px] border",
              toneClsM
            )}
          >
            {statoLabel(o.stato_maitime_aggregato)}
          </span>
        </td>
        <td className="px-3 py-2 text-xs" onClick={(e) => e.stopPropagation()}>
          <LogisticaCell
            ordine={o}
            statoLogistico={statoLogistico}
            onPassa={onPassa}
            onAnticipa={onAnticipa}
          />
        </td>
      </tr>
      {expanded && (
        <tr className="bg-accent/5">
          <td colSpan={10} className="p-0">
            <RigheNested
              righe={o.righe}
              expandedRigaId={expandedRiga}
              onToggleRiga={onToggleRiga}
            />
          </td>
        </tr>
      )}
    </>
  );
}

interface RigheNestedProps {
  righe: M1OrdineRiga[];
  expandedRigaId: string | null;
  onToggleRiga: (id: string) => void;
}

function RigheNested({ righe, expandedRigaId, onToggleRiga }: RigheNestedProps) {
  return (
    <div className="px-12 py-3 bg-muted/20 border-t border-b">
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr>
            <th className="w-6" />
            <th className="text-left font-medium px-2 py-1.5">Articolo</th>
            <th className="text-right font-medium px-2 py-1.5">Residua / Ord.</th>
            <th className="text-left font-medium px-2 py-1.5">Consegna</th>
            <th className="text-left font-medium px-2 py-1.5">Stato Maitime</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {righe.map((r) => (
            <RigaInline
              key={r.id}
              riga={r}
              expanded={expandedRigaId === r.id}
              onToggle={() => onToggleRiga(r.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RigaInline({
  riga: r,
  expanded,
  onToggle,
}: {
  riga: M1OrdineRiga;
  expanded: boolean;
  onToggle: () => void;
}) {
  const toneM = statoTone(r.stato_maitime);
  return (
    <>
      <tr className={cn("hover:bg-accent/30 cursor-pointer", expanded && "bg-accent/20")} onClick={onToggle}>
        <td className="px-2">
          {expanded ? (
            <ChevronDown className="size-3 text-muted-foreground" />
          ) : (
            <ChevronRight className="size-3 text-muted-foreground" />
          )}
        </td>
        <td className="px-2 py-1.5">
          <div className="font-mono text-[11px]">{r.codice_articolo}</div>
          {r.descrizione_articolo && (
            <div className="text-[10px] text-muted-foreground truncate max-w-[22rem]">
              {r.descrizione_articolo}
            </div>
          )}
        </td>
        <td className="px-2 py-1.5 text-right tabular-nums">
          <span className="font-medium">{formatNum(r.qty_residua)}</span>
          <span className="text-muted-foreground">/{formatNum(r.qty_ordinata)}</span>
        </td>
        <td className="px-2 py-1.5 whitespace-nowrap">{formatDataIt(r.data_consegna)}</td>
        <td className="px-2 py-1.5">
          <span
            className={cn(
              "inline-block px-1.5 py-0.5 rounded-full text-[10px] border",
              STATO_TONE_CLASS[toneM]
            )}
          >
            {statoLabel(r.stato_maitime)}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-card">
          <td colSpan={5} className="px-4 py-3">
            <DiagnosiBlock ordine={r} />
          </td>
        </tr>
      )}
    </>
  );
}

function DiagnosiBlock({ ordine }: { ordine: M1OrdineRiga }) {
  const { data, isLoading } = useM1Diagnosi(ordine.id);

  if (isLoading) return <Skeleton className="h-16 w-full" />;
  if (!data) return <p className="text-xs text-muted-foreground">Diagnosi non disponibile.</p>;

  const gap = ordine.gap_qty_maitime ? parseFloat(ordine.gap_qty_maitime) : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
      <div className="space-y-1">
        <p className="uppercase tracking-wider text-muted-foreground text-[10px]">
          Diagnosi Maitime
        </p>
        <p className="text-foreground leading-relaxed">{ordine.causale_maitime ?? "—"}</p>
        {gap > 0 && (
          <p className="text-amber-800">
            Gap residuo: <span className="font-medium">{formatNum(ordine.gap_qty_maitime)}</span>
          </p>
        )}
        {ordine.data_minima_evadibile && (
          <p className="text-muted-foreground">
            Data minima evadibile:{" "}
            <span className="font-medium text-foreground">
              {formatDataIt(ordine.data_minima_evadibile)}
            </span>
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <p className="uppercase tracking-wider text-muted-foreground text-[10px]">
          Fonti di copertura
        </p>
        <FonteRow icon={Package} label="Giacenza interna PFC" value={data.fonti.giacenza} />
        <FonteRow
          icon={CheckCircle2}
          label="Piano in tempo per consegna"
          value={data.fonti.piano_in_tempo}
        />
        <FonteRow
          icon={Clock}
          label="Piano oltre la consegna"
          value={data.fonti.piano_in_ritardo}
        />
        {data.fonti.piano_data_fine_min && (
          <p className="text-[10px] text-muted-foreground pl-5">
            Prima data piano: {formatDataIt(data.fonti.piano_data_fine_min)}
          </p>
        )}
      </div>

      <div className="space-y-1">
        <p className="uppercase tracking-wider text-muted-foreground text-[10px]">Stato</p>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground w-20">Maitime:</span>
          <span className="font-medium">{statoLabel(ordine.stato_maitime)}</span>
        </div>
      </div>
    </div>
  );
}

function FonteRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Package;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="size-3 text-muted-foreground" />
      <span className="text-muted-foreground flex-1">{label}</span>
      <span className="font-medium tabular-nums">{formatNum(value)}</span>
    </div>
  );
}

function LogisticaCell({
  ordine,
  statoLogistico,
  onPassa,
  onAnticipa,
}: {
  ordine: M1OrdineRaggruppato;
  statoLogistico: StatoLogisticoOrdine | undefined;
  onPassa: (ord: M1OrdineRaggruppato) => void;
  onAnticipa: (ord: M1OrdineRaggruppato) => void;
}) {
  const navigate = useNavigate();
  const qtyPrep = parseFloat(ordine.qty_preparata_totale ?? "0");
  const dataSped = ordine.data_spedibilita_max
    ? new Date(ordine.data_spedibilita_max)
    : null;
  const haDataSpedFutura = dataSped !== null && dataSped.getTime() > Date.now();

  // In pipeline
  if (statoLogistico?.in_pipeline) {
    const label = statoLogistico.pipeline_tipo === "anticipo" ? "Anticipo" : "Operativa";
    const icon = statoLogistico.pipeline_tipo === "anticipo" ? "⏱" : "●";
    const previsto = statoLogistico.data_pronto_prevista
      ? ` · ${new Date(statoLogistico.data_pronto_prevista).toLocaleDateString("it-IT", {
          day: "2-digit",
          month: "short",
        })}`
      : "";
    return (
      <button
        type="button"
        onClick={() => navigate(`/moduli/spedizioni?commessa=${statoLogistico.commessa_id}`)}
        className="inline-flex items-center gap-1 text-xs text-foreground hover:underline"
      >
        <span className="text-muted-foreground">{icon}</span>
        {label}{" "}
        <span className="text-muted-foreground">· {statoLogistico.stato}</span>
        {previsto && <span className="text-muted-foreground">{previsto}</span>}
      </button>
    );
  }

  // Pronta a magazzino (qty_preparata > 0) → Passa
  if (qtyPrep > 0) {
    return (
      <button
        type="button"
        onClick={() => onPassa(ordine)}
        className="text-xs px-2 py-1 rounded-md bg-blue-50 border border-blue-200 text-blue-800 hover:bg-blue-100 transition-colors inline-flex items-center gap-1"
      >
        → Passa a logistica
      </button>
    );
  }

  // Data prevista futura → Anticipa
  if (haDataSpedFutura) {
    return (
      <div className="text-xs flex items-center gap-1.5 flex-wrap">
        <span className="text-muted-foreground">
          Prevista{" "}
          {dataSped!.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
        </span>
        <button
          type="button"
          onClick={() => onAnticipa(ordine)}
          className="px-2 py-0.5 rounded-md bg-cyan-50 border border-cyan-200 text-cyan-800 hover:bg-cyan-100 transition-colors inline-flex items-center gap-1"
        >
          ⏱ Anticipa
        </button>
      </div>
    );
  }

  return <span className="text-muted-foreground text-xs">—</span>;
}

interface SortableThProps {
  label: string;
  col: OrdiniOrderBy;
  orderBy: OrdiniOrderBy;
  orderDir: OrdiniOrderDir;
  onToggle: (col: OrdiniOrderBy) => void;
  align?: "left" | "right";
}

function SortableTh({ label, col, orderBy, orderDir, onToggle, align = "left" }: SortableThProps) {
  const active = orderBy === col;
  const Icon = !active ? ArrowUpDown : orderDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={cn("font-medium px-3 py-2", align === "right" ? "text-right" : "text-left")}>
      <button
        type="button"
        onClick={() => onToggle(col)}
        className={cn(
          "inline-flex items-center gap-1 hover:text-foreground transition-colors",
          align === "right" && "flex-row-reverse",
          active && "text-foreground"
        )}
        aria-label={`Ordina per ${label}`}
      >
        {label}
        <Icon
          className={cn(
            "size-3 transition-opacity",
            active ? "opacity-100" : "opacity-30"
          )}
        />
      </button>
    </th>
  );
}

/**
 * Un riquadro della testata: è insieme il numero e il filtro.
 *
 * Prima della revisione del 16-08 la pagina aveva cinque schede in alto e una
 * fascia di chip sotto che diceva le stesse cose — la ridondanza tolta al
 * tabellone M3 nel giro di agosto. Qui il numero si clicca.
 */
function RiquadroFiltro({
  label,
  valore,
  sotto,
  tono,
  attivo,
  loading,
  disabilitato,
  onClick,
  info,
}: {
  label: string;
  valore: number | undefined;
  sotto?: string;
  tono?: "ok" | "warn" | "alert";
  attivo: boolean;
  loading?: boolean;
  disabilitato?: boolean;
  onClick?: () => void;
  info?: string;
}) {
  const tonoNumero =
    tono === "ok"
      ? "text-emerald-700 dark:text-emerald-400"
      : tono === "warn"
        ? "text-amber-700 dark:text-amber-400"
        : tono === "alert"
          ? "text-red-700 dark:text-red-400"
          : valore === 0
            ? "text-muted-foreground"
            : "text-foreground";

  return (
    <button
      type="button"
      onClick={disabilitato ? undefined : onClick}
      disabled={disabilitato}
      aria-pressed={attivo}
      title={info}
      className={cn(
        "rounded-lg border bg-card p-2.5 text-left transition-colors",
        attivo
          ? "border-brand-600 bg-brand-50/60 ring-1 ring-inset ring-brand-600 dark:bg-brand-950/40"
          : "hover:border-border hover:bg-accent/40",
        disabilitato && "cursor-default opacity-70 hover:bg-card"
      )}
    >
      <span className="block text-[11px] text-muted-foreground">{label}</span>
      {loading ? (
        <Skeleton className="my-1 h-6 w-10" />
      ) : (
        <span className={cn("block text-2xl font-semibold tabular-nums leading-tight", tonoNumero)}>
          {formatNum(valore ?? 0)}
        </span>
      )}
      {sotto && <span className="block text-[10px] text-muted-foreground">{sotto}</span>}
    </button>
  );
}

function InfoTooltip({ children }: { children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Mostra spiegazione"
          className="inline-flex items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <Info className="size-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="leading-relaxed">
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

function byStato(items: { stato: string; count: number }[] | undefined, stato: string): number {
  if (!items) return 0;
  return items.find((s) => s.stato === stato)?.count ?? 0;
}

// ── Sezione Segnali magazzino ────────────────────────────────────────
